import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import "../../styles/logistics.css";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import Layout from "../Layout.tsx";
import Modal from "../Modal.tsx";
import ContainerCounts from "../partial/ContainerCounts";
import TerminalCallWeightsAvailable from "../partial/TerminalCallWeightsAvailable";
import { removeContainer } from "../../data/containers";
import { getContainerTypes } from "../../data/containertype";
import { getTerminals } from "../../data/terminals";
import { getVessels } from "../../data/vessels";
import { getLabels } from "../../data/labels";
import { getCurrentUser } from "../../data/user";
import { showModal } from "../../data/modal";
import { Container } from "../../types/Container.ts";
import { ContainerUploadField } from "../FormFields/RenderContainerUploadFileField.tsx";
import { getVoyages } from "../../data/voyage.js";
import VoyagePicker from "../FormFields/RenderVoyagePicker.tsx";
import { updateSelectedContainer } from "../../data/selectedcontainers.js";

import {
  RenderLabels,
  RenderTerminalCallLock,
  RenderTerminalName,
  findObjectById,
  groupTerminalCalls,
  useDimensions,
} from "../Helpers.tsx";

import {
  Box,
  Button,
  CircularProgress,
  Checkbox,
  IconButton,
  Chip,
  Grid,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List as MuiList,
  ListItemButton,
  ListItemText,
  Tooltip,
} from "@mui/material";

import {
  Add as AddIcon,
  ArrowRightAlt as ArrowRightAltIcon,
  ExpandMore as ExpandMoreIcon,
  FileOpen,
  TableView as TableViewIcon,
  DeleteSweep as DeleteSweepIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

import { getSelectedTerminalCalls } from "../../data/selectedterminalcalls.js";
import { selectedFilteredVoyage } from "../../selectors/filterSelector.js";
import { normalizeIds } from "../utils/normalize.tsx";
import { useRefreshVoyageData } from "../hooks/useRefreshVoyageData.tsx";

const ACTION_TYPE_LOADING = "loading";
const ACTION_TYPE_UNLOADING = "unloading";

const getEntityId = (entity: any): string | null => {
  if (!entity) {
    return null;
  }

  if (typeof entity === "string") {
    return entity;
  }

  if (entity["@id"]) {
    return entity["@id"];
  }

  if (entity.id !== null && entity.id !== undefined) {
    return String(entity.id);
  }

  return null;
};

const getEntityIdCandidates = (entity: any): string[] => {
  if (!entity) {
    return [];
  }

  if (typeof entity === "string") {
    return [entity];
  }

  const candidates = [
    entity["@id"],
    entity.id !== null && entity.id !== undefined ? String(entity.id) : null,
  ].filter(Boolean);

  return Array.from(new Set(candidates as string[]));
};

const hasMatchingId = (selectedIds: string[] = [], entity: any) => {
  const candidates = getEntityIdCandidates(entity);

  return candidates.some((candidate) => selectedIds.includes(candidate));
};

const toggleLocalFilterId = (
  id: string | null,
  setter: React.Dispatch<React.SetStateAction<string[]>>
) => {
  if (!id) {
    return;
  }

  setter((previousIds) =>
    previousIds.includes(id)
      ? previousIds.filter((previousId) => previousId !== id)
      : [...previousIds, id]
  );
};

const getContainerLoadingTerminal = (container: any) => {
  return (
    container?.loadingTerminal ??
    container?.loadingTerminalCall?.terminal ??
    null
  );
};

const getContainerLoadingTerminalId = (container: any) => {
  return getEntityId(getContainerLoadingTerminal(container));
};

const getTerminalCallVessel = (terminalCall: any) => {
  return terminalCall?.vessel ?? null;
};

const getTerminalCallVesselId = (terminalCall: any) => {
  return getEntityId(getTerminalCallVessel(terminalCall));
};

const getTerminalCallLabelIds = (terminalCall: any) => {
  if (!Array.isArray(terminalCall?.labels)) {
    return [];
  }

  return terminalCall.labels.map(getEntityId).filter(Boolean) as string[];
};

const getContainerLabelIds = (container: any) => {
  if (!Array.isArray(container?.labels)) {
    return [];
  }

  return container.labels.map(getEntityId).filter(Boolean) as string[];
};

const hasAnyLabel = (
  entityLabels: any[] = [],
  selectedLabelIds: string[] = []
) => {
  if (!selectedLabelIds.length) {
    return true;
  }

  return entityLabels.some((label) => hasMatchingId(selectedLabelIds, label));
};

const groupContainersNested = (containers: Container[]) => {
  const outer: Record<string, Container[]> = {};

  containers.forEach((container) => {
    const loadName =
      container.loadingTerminal?.name ||
      container.loadingTerminalCall?.terminal?.name ||
      "N/A";

    (outer[loadName] ||= []).push(container);
  });

  const result: Record<
    string,
    Record<
      string,
      {
        data: Container[];
        loadingTerminal: any;
        unloadingTerminal: any;
        loading_unloadng_set: number;
      }
    >
  > = {};

  Object.entries(outer).forEach(([loadName, list]) => {
    result[loadName] = list.reduce(
      (acc, container) => {
        const key = `${
          container.loadingTerminal?.name || "N/A"
        } -> ${container.unloadingTerminal?.name || "N/A"}`;

        const entry = acc[key] || {
          data: [] as Container[],
          loadingTerminal: container.loadingTerminal,
          unloadingTerminal: container.unloadingTerminal,
          loading_unloadng_set: 0,
        };

        if (container.loadingTerminalCall && container.unloadingTerminalCall) {
          entry.loading_unloadng_set++;
        }

        if (!entry.data.includes(container)) {
          entry.data.push(container);
        }

        acc[key] = entry;

        return acc;
      },
      {} as Record<
        string,
        {
          data: Container[];
          loadingTerminal: any;
          unloadingTerminal: any;
          loading_unloadng_set: number;
        }
      >
    );
  });

  return result;
};

const Logistics: React.FC = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { voyage } = useSelector(selectedFilteredVoyage);

  const {
    terminals: { data: allTerminals },
    selectedTerminalCalls: { data: filteredTerminalCalls },
    selectedContainers: { data: filteredContainers, loading },
    vessels: { data: allVessels },
    containerTypes: { data: allTypes },
    labels: { data: allLabels },
    user: { data: userData },
    voyages: { data: voyageData },
  } = useSelector((state: any) => ({
    terminals: state.terminals,
    selectedTerminalCalls: state.selectedTerminalCalls,
    selectedContainers: state.selectedContainers,
    vessels: state.vessels,
    containerTypes: state.containerTypes,
    labels: state.labels,
    user: state.user,
    voyages: state.voyages,
  }));

  const tenant = userData?.tenant || null;

  useEffect(() => {
    [
      getTerminals,
      getContainerTypes,
      getVessels,
      getCurrentUser,
      getLabels,
      getVoyages,
    ].forEach((fn) => dispatch(fn() as any));
  }, [dispatch]);

  const { control, register } = useForm();

  const [selected, setSelected] = useState<string[]>([]);
  const [selectedContainerLabelIds, setSelectedContainerLabelIds] = useState<
    string[]
  >([]);
  const [selectedTerminalCallLabelIds, setSelectedTerminalCallLabelIds] =
    useState<string[]>([]);
  const [selectedLoadingTerminalIds, setSelectedLoadingTerminalIds] = useState<
    string[]
  >([]);
  const [selectedVesselIds, setSelectedVesselIds] = useState<string[]>([]);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);

  const draggedRef = useRef<{
    id?: string;
    action?: string;
    locked?: boolean;
  }>({});

  const availableContainerLabelIds = useMemo(() => {
    const labelIds = new Set<string>();

    (filteredContainers || []).forEach((container: any) => {
      getContainerLabelIds(container).forEach((labelId) => {
        labelIds.add(labelId);
      });
    });

    return labelIds;
  }, [filteredContainers]);

  const availableLoadingTerminalIds = useMemo(() => {
    const terminalIds = new Set<string>();

    (filteredContainers || []).forEach((container: any) => {
      const terminalId = getContainerLoadingTerminalId(container);

      if (terminalId) {
        terminalIds.add(terminalId);
      }
    });

    return terminalIds;
  }, [filteredContainers]);

  const availableVesselIds = useMemo(() => {
    const vesselIds = new Set<string>();

    (filteredTerminalCalls || []).forEach((terminalCall: any) => {
      const vesselId = getTerminalCallVesselId(terminalCall);

      if (vesselId) {
        vesselIds.add(vesselId);
      }
    });

    return vesselIds;
  }, [filteredTerminalCalls]);

  const availablePlanningLabelIds = useMemo(() => {
    const labelIds = new Set<string>();

    (filteredTerminalCalls || []).forEach((terminalCall: any) => {
      getTerminalCallLabelIds(terminalCall).forEach((labelId) => {
        labelIds.add(labelId);
      });
    });

    return labelIds;
  }, [filteredTerminalCalls]);

  useEffect(() => {
    setSelectedContainerLabelIds((previousIds) =>
      previousIds.filter((id) => availableContainerLabelIds.has(id))
    );
  }, [availableContainerLabelIds]);

  useEffect(() => {
    setSelectedTerminalCallLabelIds((previousIds) =>
      previousIds.filter((id) => availablePlanningLabelIds.has(id))
    );
  }, [availablePlanningLabelIds]);

  useEffect(() => {
    setSelectedLoadingTerminalIds((previousIds) =>
      previousIds.filter((id) => availableLoadingTerminalIds.has(id))
    );
  }, [availableLoadingTerminalIds]);

  useEffect(() => {
    setSelectedVesselIds((previousIds) =>
      previousIds.filter((id) => availableVesselIds.has(id))
    );
  }, [availableVesselIds]);

  const visibleContainerLabels = useMemo(() => {
    return allLabels.filter((label: any) => {
      const labelId = getEntityId(label);

      return (
        labelId &&
        (availableContainerLabelIds.has(labelId) ||
          selectedContainerLabelIds.includes(labelId))
      );
    });
  }, [allLabels, availableContainerLabelIds, selectedContainerLabelIds]);

  const visiblePlanningLabels = useMemo(() => {
    return allLabels.filter((label: any) => {
      const labelId = getEntityId(label);

      return (
        labelId &&
        (availablePlanningLabelIds.has(labelId) ||
          selectedTerminalCallLabelIds.includes(labelId))
      );
    });
  }, [allLabels, availablePlanningLabelIds, selectedTerminalCallLabelIds]);

  const visibleTerminals = useMemo(() => {
    return allTerminals.filter((terminal: any) => {
      const terminalId = getEntityId(terminal);

      return (
        terminalId &&
        (availableLoadingTerminalIds.has(terminalId) ||
          selectedLoadingTerminalIds.includes(terminalId))
      );
    });
  }, [allTerminals, availableLoadingTerminalIds, selectedLoadingTerminalIds]);

  const visibleVessels = useMemo(() => {
    return allVessels.filter((vessel: any) => {
      const vesselId = getEntityId(vessel);

      return (
        vesselId &&
        (availableVesselIds.has(vesselId) ||
          selectedVesselIds.includes(vesselId))
      );
    });
  }, [allVessels, availableVesselIds, selectedVesselIds]);

  const filteredContainersByActiveFilters = useMemo(() => {
    return (filteredContainers || []).filter((container: any) => {
      const matchesContainerLabels = hasAnyLabel(
        container.labels,
        selectedContainerLabelIds
      );

      const matchesLoadingTerminal =
        !selectedLoadingTerminalIds.length ||
        hasMatchingId(
          selectedLoadingTerminalIds,
          getContainerLoadingTerminal(container)
        );

      return matchesContainerLabels && matchesLoadingTerminal;
    });
  }, [
    filteredContainers,
    selectedContainerLabelIds,
    selectedLoadingTerminalIds,
  ]);

  const filteredTerminalCallsByActiveFilters = useMemo(() => {
    return (filteredTerminalCalls || []).filter((terminalCall: any) => {
      const matchesVessel =
        !selectedVesselIds.length ||
        hasMatchingId(selectedVesselIds, getTerminalCallVessel(terminalCall));

      const matchesPlanningLabels = hasAnyLabel(
        terminalCall.labels,
        selectedTerminalCallLabelIds
      );

      return matchesVessel && matchesPlanningLabels;
    });
  }, [
    filteredTerminalCalls,
    selectedVesselIds,
    selectedTerminalCallLabelIds,
  ]);

  const containerDataNested = useMemo(
    () => groupContainersNested(filteredContainersByActiveFilters),
    [filteredContainersByActiveFilters]
  );

  const terminalCallData = useMemo(
    () => groupTerminalCalls(filteredTerminalCallsByActiveFilters || []),
    [filteredTerminalCallsByActiveFilters]
  );

  const terminalsByLabel = useMemo(() => {
    return visibleTerminals.reduce(
      (acc, terminal) => {
        const labelName = terminal.labels?.[0]?.name || t("label");

        if (!acc[labelName]) {
          acc[labelName] = [];
        }

        acc[labelName].push(terminal);

        return acc;
      },
      {} as Record<string, typeof visibleTerminals>
    );
  }, [visibleTerminals, t]);

  const selectedVesselCount = selectedVesselIds.length;

  const handleToggleGroup = (groupTerminals: any[]) => {
    const groupTerminalIds = groupTerminals
      .map(getEntityId)
      .filter(Boolean) as string[];

    const allSelected = groupTerminalIds.every((terminalId) =>
      selectedLoadingTerminalIds.includes(terminalId)
    );

    setSelectedLoadingTerminalIds((previousIds) => {
      if (allSelected) {
        return previousIds.filter(
          (previousId) => !groupTerminalIds.includes(previousId)
        );
      }

      return Array.from(new Set([...previousIds, ...groupTerminalIds]));
    });
  };

  const handleSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>, id: string) => {
      setSelected((previousSelected) =>
        event.target.checked
          ? [...previousSelected, id]
          : previousSelected.filter((selectedId) => selectedId !== id)
      );
    },
    []
  );

  const handleSelectAllGroup = useCallback((ids: string[]) => {
    setSelected((previousSelected) =>
      Array.from(new Set([...previousSelected, ...ids]))
    );
  }, []);

  const refreshVoyageData = useRefreshVoyageData();

  const handleBulkDelete = useCallback(async () => {
    if (!selected.length) {
      return;
    }

    setIsDeletingSelected(true);

    await Promise.all(
      selected.map((uri) => dispatch(removeContainer({ "@id": uri }) as any))
    );

    if (voyage) {
      await refreshVoyageData(voyage);
    }

    setSelected([]);
    setIsDeletingSelected(false);
  }, [dispatch, selected, refreshVoyageData, voyage]);

  const handleTerminalLink = useCallback(() => {
    if (!selected.length) {
      return;
    }

    const containerIds = selected.map((uri) => {
      const parts = uri.split("/");
      return parts[parts.length - 1];
    });

    dispatch(
      showModal({
        title: t("containerTerminalCall"),
        description: t("linkContainersToTerminalCalls"),
        form: "containerterminalcallform",
        formProps: {
          containerIds,
          voyage,
        },
        size: "wide",
      }) as any
    );
  }, [dispatch, selected, voyage, t]);

  const dragStart = (event: React.DragEvent) => {
    const element = event.currentTarget as HTMLElement;

    draggedRef.current = {
      id: element.dataset.containerId,
      action: element.dataset.actionType,
      locked: element.classList.contains("has-logistic-call"),
    };
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();

    const { id, action, locked } = draggedRef.current;

    if (locked || !id || !action) {
      return;
    }

    const parent = (event.target as HTMLElement).closest(
      ".js-droppable-parent"
    ) as HTMLElement | null;

    if (!parent) {
      return;
    }

    const termId = parent.dataset.terminalId;
    const callId = parent.dataset.terminalCallId;

    if (!termId || !callId) {
      return;
    }

    let container = findObjectById(filteredContainers, id);

    if (!container) {
      return;
    }

    const terminal = findObjectById(allTerminals, termId);

    if (!terminal) {
      return;
    }

    container = {
      ...container,
      ...(action === ACTION_TYPE_LOADING
        ? {
            loadingTerminal: terminal,
            loadingTerminalCall: callId,
          }
        : {
            unloadingTerminal: terminal,
            unloadingTerminalCall: callId,
          }),
    };

    dispatch(updateSelectedContainer(container) as any).then(() => {
      if (voyage?.terminalCalls) {
        dispatch(
          getSelectedTerminalCalls(normalizeIds(voyage.terminalCalls)) as any
        );
      }
    });
  };

  const openModal = useCallback(
    (opts: any) => dispatch(showModal(opts) as any),
    [dispatch]
  );

  const addContainer = useCallback(
    (event: React.MouseEvent, load?: any, unload?: any) => {
      event.stopPropagation();

      openModal({
        title: t("addFormTitleByType", { type: t("container") }),
        description: t("addContainerDescription"),
        form: "container",
        formProps: {
          terminals: allTerminals,
          voyageTerminalCalls: filteredTerminalCalls,
          terminalCalls: filteredTerminalCalls,
          vessels: allVessels,
          labels: allLabels,
          selectedLoadingTerminal: load,
          selectedUnloadingTerminal: unload,
          voyages: voyageData,
          voyage,
        },
      });
    },
    [
      allTerminals,
      filteredTerminalCalls,
      allVessels,
      allLabels,
      openModal,
      voyageData,
      voyage,
      t,
    ]
  );

  const editContainer = useCallback(
    (id: string) => {
      const container = findObjectById(filteredContainers, id);

      openModal({
        title: t("editFormTitleByType", { type: t("container") }),
        description: t("editFormTitleByType", { type: t("container") }),
        form: "container",
        formProps: {
          container,
          tenant,
          terminals: allTerminals,
          vessels: allVessels,
          labels: allLabels,
          voyages: voyageData,
          voyage,
        },
      });
    },
    [
      filteredContainers,
      tenant,
      openModal,
      allTerminals,
      allVessels,
      allLabels,
      voyageData,
      voyage,
      t,
    ]
  );

  const LoadingUnloadingTitle = ({
    loadingTerminal,
    unloadingTerminal,
  }: {
    loadingTerminal: any;
    unloadingTerminal: any;
  }) => (
    <Typography
      component="span"
      variant="body2"
      display="flex"
      alignItems="center"
      gap={1}
      color="text.primary"
      noWrap
    >
      {loadingTerminal?.name ?? "N/A"}
      <ArrowRightAltIcon fontSize="small" color="primary" />
      {unloadingTerminal?.name ?? "N/A"}
    </Typography>
  );

  const ref = useRef(null);
  const { width } = useDimensions(ref);

  return (
    <Layout>
      <Modal />

      <Grid container className="main-logistic-wrapper">
        <Grid
          item
          xs={12}
          sm={2}
          className="logistic-wrapper filter-list"
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
            "&::-webkit-scrollbar": {
              width: 4,
              height: 4,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Typography variant="h6">{t("filters")}</Typography>

          <Grid item component={Paper} className="list-content-wrapper">
            <VoyagePicker includeContainers={true} />

            <Paper sx={{ marginTop: 2 }}>
              <Typography>{t("containerLabels")}</Typography>
            </Paper>

            {visibleContainerLabels.map((label: any) => {
              const labelId = getEntityId(label);
              const isSelected =
                !!labelId && selectedContainerLabelIds.includes(labelId);

              return (
                <Grid key={labelId} sx={{ display: "flex", mt: 1, ml: 1 }}>
                  <Chip
                    size="small"
                    variant={isSelected ? undefined : "outlined"}
                    label={label.name}
                    avatar={
                      <Box
                        sx={{
                          width: "10px !important",
                          height: "10px !important",
                          bgcolor: label.color || "#FFF",
                          border: "1px solid #DDD",
                        }}
                      />
                    }
                    onClick={() =>
                      toggleLocalFilterId(labelId, setSelectedContainerLabelIds)
                    }
                  />
                </Grid>
              );
            })}

            <Paper sx={{ marginTop: 2 }}>
              <Typography>{t("planningLabels")}</Typography>
            </Paper>

            {visiblePlanningLabels.map((label: any) => {
              const labelId = getEntityId(label);
              const isSelected =
                !!labelId && selectedTerminalCallLabelIds.includes(labelId);

              return (
                <Grid key={labelId} sx={{ display: "flex", mt: 1, ml: 1 }}>
                  <Chip
                    size="small"
                    variant={isSelected ? undefined : "outlined"}
                    label={label.name}
                    avatar={
                      <Box
                        sx={{
                          width: "10px !important",
                          height: "10px !important",
                          bgcolor: label.color || "#FFF",
                          border: "1px solid #DDD",
                        }}
                      />
                    }
                    onClick={() =>
                      toggleLocalFilterId(
                        labelId,
                        setSelectedTerminalCallLabelIds
                      )
                    }
                  />
                </Grid>
              );
            })}

            <Paper sx={{ marginTop: 2, marginBottom: 1 }}>
              <Typography>{t("terminals")}</Typography>
            </Paper>

            {Object.entries(terminalsByLabel).map(([label, terminals]) => {
              const allSelected = terminals.every((terminal: any) => {
                const terminalId = getEntityId(terminal);
                return (
                  !!terminalId && selectedLoadingTerminalIds.includes(terminalId)
                );
              });

              return (
                <Box key={label} sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                      ml: 1,
                      mb: 0.5,
                      cursor: "pointer",
                      userSelect: "none",
                      color: allSelected ? "primary.main" : "text.primary",
                      "&:hover": { textDecoration: "underline" },
                    }}
                    onClick={() => handleToggleGroup(terminals)}
                  >
                    {label}
                  </Typography>

                  <Grid container>
                    {terminals.map((terminal: any) => {
                      const terminalId = getEntityId(terminal);
                      const isSelected =
                        !!terminalId &&
                        selectedLoadingTerminalIds.includes(terminalId);

                      return (
                        <Grid
                          item
                          key={terminalId}
                          sx={{ display: "flex", mt: 1, ml: 1 }}
                        >
                          <Chip
                            size="small"
                            variant={isSelected ? undefined : "outlined"}
                            avatar={
                              <Box
                                sx={{
                                  width: "10px !important",
                                  height: "10px !important",
                                  bgcolor: terminal.color || "#FFF",
                                  border: "1px solid #DDD",
                                }}
                              />
                            }
                            label={terminal.name}
                            onClick={() =>
                              toggleLocalFilterId(
                                terminalId,
                                setSelectedLoadingTerminalIds
                              )
                            }
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              );
            })}
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={5}
          className="logistic-wrapper container-list"
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
            "&::-webkit-scrollbar": {
              width: 4,
              height: 4,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Typography variant="h6">{t("containersToPlan")}</Typography>

          <Grid
            ref={ref}
            item
            component={Paper}
            className="list-content-wrapper"
          >
            <Grid item className="list-content">
              <Grid
                item
                sx={{
                  maxHeight: "80vh",
                  overflow: "auto",
                  position: "relative",
                }}
              >
                <Grid
                  container
                  alignItems="center"
                  gap={1}
                  sx={{
                    position: "sticky",
                    top: 0,
                    zIndex: 5,
                    bgcolor: "background.paper",
                    p: 1,
                    borderBottom: "1px solid #DDD",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    size="small"
                    color="warning"
                    disabled={!selected.length}
                    onClick={() => setSelected([])}
                  >
                    {t("unselect")} ({selected.length})
                  </Button>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                    }}
                  >
                    <Button
                      size="small"
                      disabled={!selected.length}
                      onClick={handleTerminalLink}
                      startIcon={<TableViewIcon />}
                    >
                      {t("plan")}
                    </Button>

                    <Button
                      size="small"
                      color="error"
                      disabled={!selected.length || isDeletingSelected}
                      onClick={handleBulkDelete}
                      startIcon={
                        isDeletingSelected ? (
                          <CircularProgress color="inherit" size={16} />
                        ) : (
                          <DeleteSweepIcon />
                        )
                      }
                    >
                      {isDeletingSelected ? t("deleting") : t("delete")}
                    </Button>
                  </Box>
                </Grid>

                {Object.entries(containerDataNested).map(
                  ([loadingTerminal, groups], groupIndex) => (
                    <Accordion key={loadingTerminal + groupIndex} defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-${groupIndex}-load`}
                        id={`panel-${groupIndex}-load`}
                      >
                        <Typography color="primary">
                          <strong>{loadingTerminal}</strong>
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails sx={{ pt: 0 }}>
                        {Object.entries(groups).map(
                          ([key, group], nestedGroupIndex) => (
                            <Accordion key={key + nestedGroupIndex}>
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel-${groupIndex}-${nestedGroupIndex}`}
                                id={`panel-${groupIndex}-${nestedGroupIndex}`}
                              >
                                <Grid
                                  sx={{
                                    display: "flex",
                                    width: "100%",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <LoadingUnloadingTitle
                                    loadingTerminal={group.loadingTerminal}
                                    unloadingTerminal={group.unloadingTerminal}
                                  />

                                  <Grid
                                    className={
                                      group.data.length ===
                                      group.loading_unloadng_set
                                        ? "all-containers-planned"
                                        : ""
                                    }
                                  >
                                    <Tooltip
                                      title={t("addContainerToTrajectory")}
                                    >
                                      <IconButton
                                        size="small"
                                        onClick={(event) =>
                                          addContainer(
                                            event,
                                            group.loadingTerminal,
                                            group.unloadingTerminal
                                          )
                                        }
                                      >
                                        <AddIcon sx={{ fontSize: 20 }} />
                                      </IconButton>
                                    </Tooltip>

                                    {`${group.loading_unloadng_set}/${group.data.length}`}
                                  </Grid>
                                </Grid>
                              </AccordionSummary>

                              <AccordionDetails sx={{ pt: 0 }}>
                                <Grid
                                  container
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mb={2}
                                  height="35px"
                                >
                                  <Box
                                    display="flex"
                                    justifyContent="flex-end"
                                    gap={1}
                                    p={1}
                                  >
                                    <Button
                                      size="small"
                                      color="warning"
                                      disabled={
                                        !group.data.some((container) =>
                                          selected.includes(container["@id"])
                                        )
                                      }
                                      onClick={() => {
                                        const groupIds = group.data.map(
                                          (container) => container["@id"]
                                        );

                                        setSelected((previousSelected) =>
                                          previousSelected.filter(
                                            (id) => !groupIds.includes(id)
                                          )
                                        );
                                      }}
                                    >
                                      {t("unselect")} (
                                      {
                                        group.data.filter((container) =>
                                          selected.includes(container["@id"])
                                        ).length
                                      }
                                      )
                                    </Button>
                                  </Box>

                                  <Box>
                                    <Button
                                      size="small"
                                      color="primary"
                                      onClick={() =>
                                        handleSelectAllGroup(
                                          group.data.map(
                                            (container) => container["@id"]
                                          )
                                        )
                                      }
                                    >
                                      {t("selectAll")}
                                    </Button>
                                  </Box>
                                </Grid>

                                {group.data.map((container) => (
                                  <Grid
                                    key={container["@id"]}
                                    className={`js-search-element-${groupIndex}-${nestedGroupIndex}`}
                                    item
                                    sx={{
                                      border: "1px solid #DDD",
                                      p: 0.5,
                                      mb: 0.5,
                                      bgcolor: "rgba(221,221,221,0.2)",
                                      borderRadius: 1,
                                      position: "relative",
                                      "&:hover .select-checkbox, .select-checkbox.Mui-checked":
                                        {
                                          display: "flex",
                                        },
                                    }}
                                  >
                                    <Checkbox
                                      className="select-checkbox"
                                      checked={selected.includes(
                                        container["@id"]
                                      )}
                                      onChange={(event) =>
                                        handleSelect(event, container["@id"])
                                      }
                                      size="small"
                                      sx={{
                                        display: "none",
                                        position: "absolute",
                                        top: -12,
                                        left: -10,
                                        width: 24,
                                        height: 24,
                                        bgcolor: "rgba(0,0,0,0.4)",
                                        "&:hover": {
                                          bgcolor: "rgba(0,0,0,0.6)",
                                        },
                                        color: "#fff",
                                        "&.Mui-checked": {
                                          bgcolor: "primary.main",
                                          color: "#fff",
                                          borderColor: "primary.dark",
                                        },
                                        "& .MuiSvgIcon-root": {
                                          fontSize: 14,
                                        },
                                      }}
                                    />

                                    <Grid item sx={{ display: "flex", gap: 1 }}>
                                      <Grid
                                        className="logistic-container"
                                        onClick={() =>
                                          editContainer(container["@id"])
                                        }
                                      >
                                        <Typography
                                          display="flex"
                                          alignItems="center"
                                          gap={1}
                                          component="span"
                                          variant="body2"
                                          color="text.primary"
                                          noWrap
                                          fontWeight={700}
                                        >
                                          {container.uid}

                                          {container.importFile && (
                                            <Tooltip
                                              title={`${container.importFile}`}
                                            >
                                              <FileOpen
                                                size="small"
                                                fontSize="14px"
                                                sx={{
                                                  color: "#008000",
                                                }}
                                              />
                                            </Tooltip>
                                          )}
                                        </Typography>

                                        <small>
                                          {container.containerType?.name
                                            ? `${container.containerType.name} | `
                                            : ""}
                                          {container.weight
                                            ? `${container.weight}${t("kg")}`
                                            : ""}
                                        </small>
                                      </Grid>

                                      {container.labels?.length > 0 ? (
                                        <Grid
                                          container
                                          justifyContent="center"
                                          alignItems="center"
                                        >
                                          {RenderLabels(
                                            container.labels,
                                            width
                                          )}
                                        </Grid>
                                      ) : (
                                        <Grid />
                                      )}

                                      <Grid
                                        sx={{ position: "relative" }}
                                        data-container-id={container["@id"]}
                                        data-action-type={ACTION_TYPE_LOADING}
                                        onClick={() =>
                                          container.loadingTerminalCall
                                            ? openModal({
                                                title: t("planning"),
                                                description: t("unlinkContainer"),
                                                form: "containerterminalcall",
                                                formProps: {
                                                  container,
                                                  terminalCallType:
                                                    ACTION_TYPE_LOADING,
                                                },
                                              })
                                            : undefined
                                        }
                                        onDragStart={dragStart}
                                        draggable={
                                          !loading &&
                                          !container.loadingTerminalCall
                                        }
                                        className={`logistic-in-out-default ${
                                          container.loadingTerminalCall
                                            ? "has-logistic-call"
                                            : ""
                                        }`}
                                      >
                                        {container.loadedDate && (
                                          <CheckCircleIcon
                                            color="success"
                                            fontSize="inherit"
                                            sx={{
                                              position: "absolute",
                                              right: "-4px",
                                              top: "-4px",
                                              backgroundColor: "#FFFFFF",
                                              borderRadius: "100%",
                                            }}
                                          />
                                        )}
                                        IN
                                      </Grid>

                                      <Grid
                                        sx={{ position: "relative" }}
                                        data-container-id={container["@id"]}
                                        data-action-type={ACTION_TYPE_UNLOADING}
                                        onClick={() =>
                                          container.unloadingTerminalCall
                                            ? openModal({
                                                title: t("planning"),
                                                description: t("unlinkContainer"),
                                                form: "containerterminalcall",
                                                formProps: {
                                                  container,
                                                  terminalCallType:
                                                    ACTION_TYPE_UNLOADING,
                                                },
                                              })
                                            : undefined
                                        }
                                        onDragStart={dragStart}
                                        draggable={
                                          !loading &&
                                          !container.unloadingTerminalCall
                                        }
                                        className={`logistic-in-out-default ${
                                          container.unloadingTerminalCall
                                            ? "has-logistic-call"
                                            : ""
                                        }`}
                                      >
                                        {container.unloadedDate && (
                                          <CheckCircleIcon
                                            color="success"
                                            fontSize="inherit"
                                            sx={{
                                              position: "absolute",
                                              right: "-4px",
                                              top: "-4px",
                                              backgroundColor: "#FFFFFF",
                                              borderRadius: "100%",
                                            }}
                                          />
                                        )}
                                        OUT
                                      </Grid>
                                    </Grid>
                                  </Grid>
                                ))}
                              </AccordionDetails>
                            </Accordion>
                          )
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </Grid>

              <Grid container gap={1} mt={2} flexDirection="column">
                <ContainerUploadField
                  name="containerUploadFile"
                  control={control}
                  register={register}
                  height="5vh"
                />

                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<AddIcon />}
                  onClick={(event) => addContainer(event)}
                >
                  {t("container")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={2}
          className="logistic-wrapper vessel-list"
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
            "&::-webkit-scrollbar": {
              width: 4,
              height: 4,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Typography variant="h6">{t("vessels")}</Typography>

          <Grid item component={Paper} className="list-content-wrapper">
            {selectedVesselCount > 0 && (
              <Box
                sx={{
                  p: 1,
                  borderBottom: "1px solid #DDD",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  {t("selected")}: {selectedVesselCount}
                </Typography>

                <Button
                  size="small"
                  color="warning"
                  onClick={() => setSelectedVesselIds([])}
                >
                  {t("clear")}
                </Button>
              </Box>
            )}

            <MuiList>
              {visibleVessels.map((vessel: any) => {
                const vesselId = getEntityId(vessel);
                const isSelected =
                  !!vesselId && selectedVesselIds.includes(vesselId);

                return (
                  <ListItemButton
                    key={vesselId}
                    className="vessel-list-item"
                    selected={Boolean(isSelected)}
                    onClick={() =>
                      toggleLocalFilterId(vesselId, setSelectedVesselIds)
                    }
                    sx={{
                      borderLeft: isSelected
                        ? "4px solid"
                        : "4px solid transparent",
                      borderLeftColor: isSelected
                        ? "primary.main"
                        : "transparent",
                      backgroundColor: isSelected
                        ? "action.selected"
                        : "transparent",
                      "&:hover": {
                        backgroundColor: isSelected
                          ? "action.selected"
                          : "action.hover",
                      },
                    }}
                  >
                    <ListItemText primary={vessel.name} />
                  </ListItemButton>
                );
              })}
            </MuiList>
          </Grid>
        </Grid>

        <Grid
          item
          xs={12}
          sm={7}
          className="logistic-wrapper planning-list"
          sx={{
            scrollbarWidth: "thin",
            scrollbarColor: "#888 transparent",
            "&::-webkit-scrollbar": {
              width: 4,
              height: 4,
            },
            "&::-webkit-scrollbar-track": {
              background: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "#888",
              borderRadius: 2,
            },
            "&::-webkit-scrollbar-thumb:hover": {
              backgroundColor: "#555",
            },
          }}
        >
          <Typography variant="h6">
            {t("vesselPlanning")}
            {selectedVesselCount > 0 || selectedTerminalCallLabelIds.length > 0
              ? ` (${filteredTerminalCallsByActiveFilters.length})`
              : ""}
          </Typography>

          <Grid item component={Paper} className="list-content-wrapper">
            <Grid item className="list-content">
              <Grid item sx={{ maxHeight: "80vh", overflow: "auto" }}>
                {Object.entries(terminalCallData).map(
                  ([key, calls], terminalCallIndex) => (
                    <Accordion key={key + terminalCallIndex} defaultExpanded>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls={`panel-${terminalCallIndex}-content`}
                        id={`panel-${terminalCallIndex}-header`}
                      >
                        <Grid
                          sx={{
                            display: "flex",
                            width: "100%",
                            justifyContent: "space-between",
                          }}
                        >
                          <Grid>{key}</Grid>
                        </Grid>
                      </AccordionSummary>

                      <AccordionDetails>
                        {calls.map((call) => (
                          <Grid
                            key={call["@id"]}
                            className="planning-terminal-item js-droppable-parent"
                            component={Paper}
                            data-terminal-id={call.terminal["@id"]}
                            data-terminal-call-id={call["@id"]}
                            onDragOver={(event) =>
                              !call.loadingPlanLocked &&
                              event.preventDefault()
                            }
                            onDrop={(event) =>
                              !call.loadingPlanLocked && onDrop(event)
                            }
                            onClick={() =>
                              openModal({
                                title: t("editFormTitleByType", {
                                  type: t("planning"),
                                }),
                                description: t("editFormTitleByType", {
                                  type: t("planning"),
                                }),
                                form: "terminalCall",
                                formProps: {
                                  terminalCall: call,
                                  terminals: allTerminals,
                                  vessels: allVessels,
                                  labels: allLabels,
                                  voyages: voyageData,
                                  voyage,
                                },
                              })
                            }
                          >
                            <Grid container justifyContent="space-between">
                              {call.labels?.length > 0 ? (
                                <Grid item>{RenderLabels(call.labels)}</Grid>
                              ) : (
                                <Grid />
                              )}

                              {!call.departure &&
                              call.status !== "Completed" ? (
                                <Button
                                  color="success"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    openModal({
                                      title: t("terminalCallCompletion"),
                                      description: t(
                                        "markTerminalCallAsComplete"
                                      ),
                                      form: "terminalcallcompletion",
                                      formProps: { terminalCall: call },
                                    });
                                  }}
                                >
                                  {t("markAsCompleted")}
                                </Button>
                              ) : (
                                ""
                              )}
                            </Grid>

                            <Grid
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                p: 1,
                                alignItems: "center",
                                borderBottom: "1px solid #DDDDDD",
                              }}
                            >
                              <Grid
                                sx={{
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                {RenderTerminalCallLock(call)}
                                {RenderTerminalName(call.terminal, true)}
                              </Grid>

                              <Grid>
                                {dayjs(call.planned || call.arrival).format(
                                  "HH:mm"
                                )}

                                {call.departure &&
                                call.status === "Completed"
                                  ? ` - ${dayjs(call.departure).format(
                                      "HH:mm"
                                    )}`
                                  : ""}
                              </Grid>
                            </Grid>

                            <Grid
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                              }}
                            >
                              <Grid container flexWrap="nowrap">
                                <Grid className="planning-terminal-action">
                                  <ContainerCounts
                                    containers={filteredContainers.filter(
                                      (container) =>
                                        call.loadingContainers.some(
                                          (loadingContainer) =>
                                            loadingContainer["@id"] ===
                                            container["@id"]
                                        )
                                    )}
                                    containerTypes={allTypes}
                                    loading
                                  />
                                </Grid>

                                <Grid className="planning-terminal-action">
                                  <ContainerCounts
                                    containers={filteredContainers.filter(
                                      (container) =>
                                        call.unloadingContainers.some(
                                          (unloadingContainer) =>
                                            unloadingContainer["@id"] ===
                                            container["@id"]
                                        )
                                    )}
                                    containerTypes={allTypes}
                                    loading={false}
                                  />
                                </Grid>
                              </Grid>

                              <Grid container flexWrap="nowrap">
                                <Grid
                                  className="planning-terminal-action"
                                  width="100% !important"
                                >
                                  <TerminalCallWeightsAvailable
                                    terminalCall={call}
                                    containers={filteredContainers}
                                  />
                                </Grid>
                              </Grid>
                            </Grid>
                          </Grid>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )
                )}
              </Grid>

              <Grid container gap={1} mt={2} flexDirection="column">
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() =>
                    openModal({
                      title: t("addNewPlanning"),
                      description: t("createPlanning"),
                      form: "terminalCall",
                      formProps: {
                        tenant,
                        terminals: allTerminals,
                        vessels: allVessels,
                        labels: allLabels,
                        voyages: voyageData,
                        voyage,
                      },
                    })
                  }
                >
                  {t("planning")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Layout>
  );
};

export default Logistics;